if (!customElements.get('anniversary-milestones')) {
  class AnniversaryMilestones extends HTMLElement {
    connectedCallback() {
      this.cleanup();

      this.section = this.closest('.anniversary-milestones');
      this.fullPinTarget = this.querySelector('[data-milestones-pin]');
      this.scrollStage = this.querySelector('[data-milestones-scroll-stage]');
      this.pinTarget = this.fullPinTarget;
      this.inner = this.querySelector('.anniversary-milestones__inner');
      this.viewport = this.querySelector('[data-milestones-viewport]');
      this.track = this.querySelector('[data-milestones-track]');
      this.axis = this.querySelector('[data-milestones-axis]');
      this.range = this.querySelector('[data-milestones-range]');
      this.toggle = this.querySelector('[data-milestones-toggle]');
      this.toggleLabel = this.querySelector('[data-milestones-toggle-label]');
      this.items = [...this.querySelectorAll('[data-milestone-item]')];
      this.groups = [...this.querySelectorAll('[data-milestone-group]')];
      this.axisNodes = [...this.querySelectorAll('[data-milestones-axis-node]')];
      this.lastItem = this.items[this.items.length - 1];

      if (!this.section || !this.fullPinTarget || !this.scrollStage || !this.inner || !this.viewport || !this.track || !this.axis || !this.range || !this.items.length) return;

      this.tabletQuery = matchMedia('(min-width: 768px)');
      this.pcQuery = matchMedia('(min-width: 1280px)');
      this.reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileInitialCount = Number(this.dataset.mobileInitialCount) || 5;
      this.moreLabel = this.dataset.moreLabel || 'More';
      this.lessLabel = this.dataset.lessLabel || 'Less';
      this.baseHeaderOffset = Number.parseFloat(
        getComputedStyle(this.section).getPropertyValue('--anniversary-milestones-header-offset')
      ) || 60;
      this.mobileStage = 0;
      this.progress = 0;
      this.motionTriggerId = `anniversary-milestones-motion-${this.section.id}`;
      this.abortController = new AbortController();
      this.syncHeaderOffset();

      this.onRangeInput = this.onRangeInput.bind(this);
      this.onAxisPointerDown = this.onAxisPointerDown.bind(this);
      this.onAxisPointerMove = this.onAxisPointerMove.bind(this);
      this.onAxisPointerUp = this.onAxisPointerUp.bind(this);
      this.onViewportScroll = this.onViewportScroll.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.requestRebuild = this.requestRebuild.bind(this);

      const { signal } = this.abortController;
      this.range.addEventListener('input', this.onRangeInput, { signal });
      this.axis.addEventListener('pointerdown', this.onAxisPointerDown, { signal });
      window.addEventListener('pointermove', this.onAxisPointerMove, { signal });
      window.addEventListener('pointerup', this.onAxisPointerUp, { signal });
      window.addEventListener('pointercancel', this.onAxisPointerUp, { signal });
      this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true, signal });
      this.toggle?.addEventListener('click', this.onToggle, { signal });
      this.tabletQuery.addEventListener('change', this.onModeChange, { signal });
      this.pcQuery.addEventListener('change', this.onModeChange, { signal });
      this.reducedMotionQuery.addEventListener('change', this.onModeChange, { signal });
      window.addEventListener('resize', this.requestRebuild, { passive: true, signal });
      window.addEventListener('load', this.requestRebuild, { once: true, signal });

      this.classList.add('is-enhanced');
      this.updateMobileItems();
      this.buildExperience();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this.abortController?.abort();
      this.abortController = null;
      this.mobileObserver?.disconnect();
      this.mobileObserver = null;
      this.destroyMotion();
      this.isAxisDragging = false;
      this.activePointerId = null;
      this.nativeScroll = false;
      this.compactHeight = false;
      this.scrollDistance = 0;
      cancelAnimationFrame(this.rebuildFrame);
      cancelAnimationFrame(this.scrollCompensationFrame);
      cancelAnimationFrame(this.axisSyncFrame);
      cancelAnimationFrame(this.refreshFrame);
      clearTimeout(this.mobileRevealFallback);
      this.rebuildFrame = null;
      this.scrollCompensationFrame = null;
      this.axisSyncFrame = null;
      this.refreshFrame = null;
      if (this.headerOffsetWasSet) {
        this.section?.style.removeProperty('--anniversary-milestones-header-offset');
        this.headerOffsetWasSet = false;
      }
      this.section?.classList.remove('is-compact-height');
      this.classList.remove('is-compact-height');
    }

    isMobile() {
      return !this.tabletQuery.matches;
    }

    isTablet() {
      return this.tabletQuery.matches && !this.pcQuery.matches;
    }

    requestRebuild(force = false) {
      if (typeof force !== 'boolean') force = false;
      cancelAnimationFrame(this.rebuildFrame);
      this.rebuildFrame = requestAnimationFrame(() => {
        this.syncHeaderOffset();
        const compactHeight = this.shouldUseCompactHeight();
        if (!force && this.motionTrigger && window.ScrollTrigger && compactHeight === this.compactHeight) {
          window.ScrollTrigger.refresh();
          return;
        }

        this.buildExperience();
      });
    }

    onModeChange() {
      this.mobileStage = 0;
      this.progress = 0;
      this.updateMobileItems();
      this.requestRebuild(true);
    }

    buildExperience() {
      this.destroyMotion();
      this.nativeScroll = false;
      this.mobileObserver?.disconnect();
      this.mobileObserver = null;
      this.classList.remove('is-mobile-reveal');
      this.classList.remove('is-at-end');
      this.section.classList.remove('is-scroll-pinned');
      this.section.classList.remove('is-native-scroll');
      this.section.classList.remove('is-compact-height');
      this.classList.remove('is-compact-height');
      this.compactHeight = false;
      this.pinTarget = this.fullPinTarget;
      this.section.style.removeProperty('--anniversary-milestones-active-start-gap');
      this.viewport.scrollLeft = 0;
      this.track.style.transform = '';
      this.syncAxisNodes();
      cancelAnimationFrame(this.axisSyncFrame);
      this.axisSyncFrame = requestAnimationFrame(() => this.syncAxisNodes());

      if (this.isMobile()) {
        this.setupMobileReveal();
        return;
      }

      if (this.isTablet()) {
        this.setupNativeScrollExperience();
        return;
      }

      if (this.reducedMotionQuery.matches) {
        this.setupNativeScrollExperience();
        return;
      }

      if (!window.gsap || !window.ScrollTrigger) {
        this.setupNativeScrollExperience();
        return;
      }

      this.compactHeight = this.shouldUseCompactHeight();
      this.section.classList.toggle('is-compact-height', this.compactHeight);
      this.classList.toggle('is-compact-height', this.compactHeight);
      this.pinTarget = this.fullPinTarget;

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);

      const updateScrollDistance = () => {
        const distance = this.getTravelDistance();
        this.scrollDistance = distance;
        return distance;
      };

      if (updateScrollDistance() <= 0) {
        this.revealAll();
        return;
      }

      this.section.classList.add('is-scroll-pinned');
      this.motionContext = gsap.context(() => {
        this.motionTween = gsap.to(this.track, {
          x: () => -updateScrollDistance(),
          ease: 'none',
          scrollTrigger: {
            id: this.motionTriggerId,
            trigger: this.pinTarget,
            start: () => `top top+=${this.getHeaderOffset()}`,
            end: () => `+=${updateScrollDistance()}`,
            pin: this.pinTarget,
            pinSpacing: true,
            anticipatePin: 1,
            scrub: 0.75,
            refreshPriority: -10,
            invalidateOnRefresh: true,
            onUpdate: (trigger) => this.setProgress(trigger.progress),
          },
        });
        this.motionTrigger = this.motionTween.scrollTrigger;

        this.items.forEach((item, index) => {
          const itemTrigger = index === 0
            ? {
                trigger: this.pinTarget,
                start: 'top 85%',
                end: 'top 45%',
                scrub: 0.35,
              }
            : {
                trigger: item,
                start: 'left 88%',
                end: 'left 56%',
                containerAnimation: this.motionTween,
                scrub: 0.35,
              };

          gsap.fromTo(
            item,
            { autoAlpha: 0, y: 40 },
            {
              autoAlpha: 1,
              y: 0,
              ease: 'none',
              scrollTrigger: itemTrigger,
            }
          );
        });
      }, this);

      this.setProgress(0);
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.motionTrigger) return;
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      });
    }

    destroyMotion() {
      this.motionContext?.revert();
      this.motionContext = null;
      this.motionTween = null;
      this.motionTrigger = null;
      this.section?.classList.remove('is-scroll-pinned');
      this.fullPinTarget?.removeAttribute('style');
      this.scrollStage?.removeAttribute('style');
      this.track?.style.removeProperty('transform');
    }

    shouldUseCompactHeight() {
      if (!this.pcQuery?.matches || !this.inner) return false;

      const innerStyles = getComputedStyle(this.inner);
      const padding = Number.parseFloat(innerStyles.paddingBlockStart) + Number.parseFloat(innerStyles.paddingBlockEnd);
      const rowGap = Number.parseFloat(innerStyles.rowGap) || 0;
      const headerHeight = this.querySelector('.anniversary-milestones__header')?.offsetHeight || 0;
      const timelineHeight = Math.max(this.viewport?.offsetHeight || 0, this.track?.offsetHeight || 0);
      const axisHeight = this.axis?.offsetHeight || 0;
      const finePrintHeight = this.querySelector('.anniversary-milestones__fine-print')?.offsetHeight || 0;
      const requiredHeight = padding + headerHeight + timelineHeight + axisHeight + finePrintHeight + rowGap * 3;
      const availableHeight = window.innerHeight - this.getHeaderOffset();

      return requiredHeight > availableHeight;
    }

    getTravelDistance() {
      if (!this.pcQuery.matches) {
        return Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      const axisRect = this.axis.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const lastItemRect = this.lastItem?.getBoundingClientRect();
      if (!lastItemRect) return 0;

      const lastItemRightInTrack = lastItemRect.right - trackRect.left;
      return Math.max(0, viewportRect.left + lastItemRightInTrack - axisRect.right);
    }

    getHeaderOffset() {
      const value = getComputedStyle(this.section).getPropertyValue('--anniversary-milestones-header-offset');
      return Number.parseFloat(value) || 60;
    }

    syncHeaderOffset() {
      if (!this.section) return;

      // The anniversary anchor bar replaces the global header as the visible sticky obstruction.
      const anchorTab = document.querySelector('.shopify-section__anchor_tab_responsive');
      const anchorTabHeight = anchorTab?.getBoundingClientRect().height || 0;
      const offset = Math.max(this.baseHeaderOffset || 60, anchorTabHeight);

      this.section.style.setProperty('--anniversary-milestones-header-offset', `${offset}px`);
      this.headerOffsetWasSet = true;
    }

    syncAxisNodes() {
      if (this.isMobile() || this.items.length < 2 || this.axisNodes.length !== this.items.length) return;

      const axisWidth = this.axis.getBoundingClientRect().width;
      if (axisWidth <= 0) return;
      const step = axisWidth / (this.items.length - 1);

      this.axisNodes.forEach((node, index) => {
        node.style.setProperty('--milestone-position', `${step * index}px`);
      });
    }

    setupNativeScrollExperience() {
      this.nativeScroll = true;
      this.section.classList.add('is-native-scroll');
      this.items.forEach((item) => {
        item.style.removeProperty('opacity');
        item.style.removeProperty('visibility');
        item.style.removeProperty('transform');
      });
      this.revealAll();
      this.syncNativeProgress();
    }

    setupMobileReveal() {
      this.classList.add('is-mobile-reveal');
      this.mobileObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('is-revealed');
          });
        },
        { threshold: 0.15 }
      );
      this.items.forEach((item) => this.mobileObserver.observe(item));
      clearTimeout(this.mobileRevealFallback);
      this.mobileRevealFallback = window.setTimeout(() => {
        this.items.forEach((item) => {
          if (item.dataset.mobileVisible === 'true') item.classList.add('is-revealed');
        });
      }, 500);
    }

    revealAll() {
      this.items.forEach((item) => item.classList.add('is-revealed'));
    }

    setProgress(progress) {
      this.progress = Math.min(1, Math.max(0, progress));
      this.style.setProperty('--anniversary-milestones-progress', this.progress.toFixed(4));
      this.range.value = String(Math.round(this.progress * 1000));
      this.syncEndRevealState();
    }

    syncEndRevealState() {
      const isAtEnd = this.pcQuery.matches && this.progress >= 0.995;
      this.classList.toggle('is-at-end', isAtEnd);

      if (!isAtEnd || !this.lastItem || !window.gsap) return;

      window.gsap.set(this.lastItem, { autoAlpha: 1, y: 0 });
    }

    syncNativeProgress() {
      if (!this.nativeScroll) return;
      const maxScroll = Math.max(1, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.setProgress(this.viewport.scrollLeft / maxScroll);
    }

    onViewportScroll() {
      this.syncNativeProgress();
    }

    onRangeInput() {
      const progress = Number(this.range.value) / 1000;
      this.applyTimelineProgress(progress);
    }

    getAxisProgress(clientX) {
      const rect = this.axis.getBoundingClientRect();
      const thumb = this.querySelector('.anniversary-milestones__axis-thumb');
      const measuredThumbWidth = thumb?.getBoundingClientRect().width || 48;
      const thumbWidth = Math.min(measuredThumbWidth, rect.width);
      const availableWidth = Math.max(1, rect.width - thumbWidth);
      return (clientX - rect.left - thumbWidth / 2) / availableWidth;
    }

    startAxisDrag(clientX) {
      if (this.isMobile()) return false;

      this.isAxisDragging = true;
      this.activePointerId = null;
      this.classList.add('is-axis-dragging');
      this.range.focus({ preventScroll: true });
      this.applyTimelineProgress(this.getAxisProgress(clientX));
      return true;
    }

    onAxisPointerDown(event) {
      if (!event.isPrimary || event.button !== 0) return;
      if (!this.startAxisDrag(event.clientX)) return;

      this.activePointerId = event.pointerId;
      this.axis.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    onAxisPointerMove(event) {
      if (!this.isAxisDragging || event.pointerId !== this.activePointerId) return;

      this.applyTimelineProgress(this.getAxisProgress(event.clientX));
      event.preventDefault();
    }

    onAxisPointerUp(event) {
      if (!this.isAxisDragging || event.pointerId !== this.activePointerId) return;

      this.isAxisDragging = false;
      this.activePointerId = null;
      this.classList.remove('is-axis-dragging');
      if (this.axis.hasPointerCapture?.(event.pointerId)) {
        this.axis.releasePointerCapture(event.pointerId);
      }
    }

    applyTimelineProgress(progress) {
      const safeProgress = Math.min(1, Math.max(0, progress));

      if (this.motionTrigger) {
        const destination = this.motionTrigger.start + (this.motionTrigger.end - this.motionTrigger.start) * safeProgress;
        window.scrollTo({ top: destination, behavior: 'auto' });
        return;
      }

      if (this.nativeScroll) {
        const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        this.viewport.scrollLeft = maxScroll * safeProgress;
        this.setProgress(safeProgress);
        return;
      }

      this.setProgress(safeProgress);
    }

    getMobileVisibleCount(stage = this.mobileStage) {
      const singleExpansion = this.items.length <= 20;
      if (stage === 0) return this.mobileInitialCount;
      if (stage === 1) return singleExpansion ? this.items.length : Math.min(this.items.length, this.mobileInitialCount + 10);
      return this.items.length;
    }

    onToggle() {
      const previousCount = this.getMobileVisibleCount();
      const previousToggleTop = this.toggle.getBoundingClientRect().top;
      const singleExpansion = this.items.length <= 20;

      if (singleExpansion) {
        this.mobileStage = this.mobileStage === 0 ? 1 : 0;
      } else if (this.mobileStage === 0) {
        this.mobileStage = 1;
      } else if (this.mobileStage === 1) {
        this.mobileStage = 2;
      } else {
        this.mobileStage = 0;
      }

      this.updateMobileItems();
      const nextCount = this.getMobileVisibleCount();
      const isCollapsing = nextCount < previousCount;
      const revealedItems = this.items.slice(previousCount, nextCount);

      if (!isCollapsing && !this.reducedMotionQuery.matches) {
        revealedItems.forEach((item) => item.classList.remove('is-revealed'));
        requestAnimationFrame(() => {
          revealedItems.forEach((item) => item.classList.add('is-revealed'));
        });
      }

      if (!isCollapsing) return;

      cancelAnimationFrame(this.scrollCompensationFrame);
      this.scrollCompensationFrame = requestAnimationFrame(() => {
        const nextToggleTop = this.toggle.getBoundingClientRect().top;
        window.scrollBy({ top: nextToggleTop - previousToggleTop, behavior: 'auto' });
      });
    }

    updateMobileItems() {
      if (!this.isMobile() || this.reducedMotionQuery.matches) {
        this.items.forEach((item) => {
          item.dataset.mobileVisible = 'true';
        });
        this.groups.forEach((group) => {
          group.hidden = false;
        });
        if (this.toggle) this.toggle.hidden = true;
        return;
      }

      const visibleCount = this.getMobileVisibleCount();
      this.items.forEach((item, index) => {
        item.dataset.mobileVisible = String(index < visibleCount);
      });
      this.groups.forEach((group) => {
        group.hidden = ![...group.querySelectorAll('[data-milestone-item]')].some(
          (item) => item.dataset.mobileVisible === 'true'
        );
      });

      if (!this.toggle || !this.toggleLabel) return;
      this.toggle.hidden = this.items.length <= this.mobileInitialCount;
      const singleExpansion = this.items.length <= 20;
      const isExpanded = singleExpansion ? this.mobileStage === 1 : this.mobileStage === 2;
      this.toggleLabel.textContent = isExpanded ? this.lessLabel : this.moreLabel;
      this.toggle.classList.toggle('is-less', isExpanded);
    }
  }

  customElements.define('anniversary-milestones', AnniversaryMilestones);
}
